// @flow
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "./useRouter";

import { createHref, filterParameters } from "../utils/helpers/createHref";

import ModelCatalogModel from "../models/modelcatalog/ModelCatalogModel";
import ConceptIndexModel from "../models/concepts/ConceptIndexModel";
import ConceptDetailModel from "../models/concepts/ConceptDetailModel";
import { default as BusinessScenarioModel } from "../models/concepts/BusinessScenarioModel";
import ContentIndexModel from "../models/content/ContentIndexModel";
import ContentTOCModel from "../models/content/ContentTOCModel";
import ContentModel from "../models/content/ContentModel";

import { useModularUIBasic } from "./useModularUIBasic";
import { ContentTypeModel } from "../models";

import { getEntryDate } from "../redux/selectors/modelcatalog";
import { updateEntryDate } from "../redux/actions/ModelCatalog";

import Href from "../models/href/Href";
import type { UseModularUIBasicOptions } from "./useModularUIBasic";
import type { UpdateEntryDateAction } from "../redux";
export type SearchFilter = {
  index?: string,
  label?: string,
  type?: string | Array<string>,
  modelCategory?: string | Array<string>,
  entryDate?: string,
};

/**
 */
export const useModelCatalog = (
  options?: UseModularUIBasicOptions<ModelCatalogModel>,
): ?ModelCatalogModel =>
  useModularUIBasic("modelcatalog", "/modelcatalog", {
    expectedModels: ["ModelCatalog"],
    targetModel: ModelCatalogModel,
    ...options,
  });

/**
 */
export const useConceptIndex = (
  href: string | Href,
  key?: string = "conceptindex",
  options?: UseModularUIBasicOptions<ConceptIndexModel>,
): ?ConceptIndexModel => {
  const { entryDate } = useEntryDate();
  const url = filterParameters(href, [
    "index",
    "label",
    "modelcategory",
    "type",
  ]);
  url.setParameter("entryDate", entryDate);

  return useModularUIBasic(key, url, {
    expectedModels: ["ConceptIndex"],
    targetModel: ConceptIndexModel,
    ...options,
  });
};

/**
 */
export const useConceptDetail = (
  concept: string | Href,
  key?: string = "conceptdetail",
  options?: UseModularUIBasicOptions<
    ConceptDetailModel | BusinessScenarioModel,
  >,
): ?ConceptDetailModel | ?BusinessScenarioModel => {
  const { entryDate } = useEntryDate();
  const location = useLocation();

  const href = createHref("concepts", concept, entryDate, location, []);

  return useModularUIBasic(key, href, {
    expectedModels: ["ConceptDetail", "BusinessScenario"],
    ...options,
  });
};

/**
 */
export const useContentIndex = (
  href: string | Href,
  key?: string = "contentindex",
  options?: UseModularUIBasicOptions<ContentIndexModel>,
): ?ContentIndexModel => {
  const url = filterParameters(href, ["index", "label", "type"]);
  return useModularUIBasic(key, url, {
    expectedModels: ["ContentIndex"],
    targetModel: ContentIndexModel,
    ...options,
  });
};

/**
 */
export const useContentTOC = (
  content: string | Href,
  key?: string = "contenttoc",
  options?: UseModularUIBasicOptions<ContentTOCModel>,
): ?ContentTOCModel => {
  const { entryDate } = useEntryDate();
  const location = useLocation();

  const href = createHref("content", content, entryDate, location, [
    "complete",
  ]);

  return useModularUIBasic(key, href, {
    expectedModels: ["ContentTOC"],
    targetModel: ContentTOCModel,
    ...options,
  });
};

/**
 */
export const useContent = (
  content: string | Href,
  key?: string = "content",
  options?: UseModularUIBasicOptions<ContentModel>,
): ?ContentModel | ?ContentTOCModel => {
  const { entryDate } = useEntryDate();
  const location = useLocation();

  const href = createHref("content", content, entryDate, location, [
    "complete",
  ]);

  return useModularUIBasic(key, href, {
    expectedModels: ["Content", "ContentTOC"],
    ...options,
  });
};

/**
 */
export const useCompleteSource = (
  content: string | Href,
  key?: string = "content",
  options?: UseModularUIBasicOptions<ContentModel>,
): ?ContentModel | ?ContentTOCModel => {
  const { entryDate } = useEntryDate();
  const location = useLocation();

  let contentHref = new Href(content);
  contentHref.removeParameter("complete");
  contentHref.addParameter("complete", "true");

  const href = createHref("content", contentHref, entryDate, location, [
    "complete",
  ]);

  return useModularUIBasic(key, href, {
    expectedModels: ["Content", "ContentTOC"],
    ...options,
  });
};

/**
 */
export const useContentType = (
  contentType: string | Href,
  key?: string = "contenttypes",
  options?: UseModularUIBasicOptions<ContentTypeModel>,
): ?ContentTypeModel => {
  const href = createHref("contenttypes", contentType, null, null, []);
  return useModularUIBasic(key, href, {
    expectedModels: ["ContentType"],
    targetModel: ContentTypeModel,
    ...options,
  });
};

/**
 */
const getSearchHref = (uri: string, filters: SearchFilter): string => {
  const filterString = [];

  for (const key of Object.keys(filters)) {
    const filterValue = filters[key];
    if (filterValue) {
      let value: string = Array.isArray(filterValue)
        ? filterValue.join(",")
        : filterValue;
      if (key === "type") {
        value = value.replace("#", "%23"); // handles un-encoded #
      }
      filterString.push(`${key}=${value}`);
    }
  }

  return filterString.length ? `${uri}?${filterString.join("&")}` : uri;
};

/**
 */
export const useConceptSearch = (
  filters: SearchFilter,
  key?: string = "conceptSearch",
  options?: UseModularUIBasicOptions<ConceptIndexModel>,
): ?ConceptIndexModel => {
  const href = getSearchHref("/concepts", filters);
  return useConceptIndex(href, key, options);
};

/**
 */
export const useContentSearch = (
  filters: SearchFilter,
  key?: string = "contentSearch",
  options?: UseModularUIBasicOptions<ContentIndexModel>,
): ?ContentIndexModel => {
  const href = getSearchHref("/content", filters);
  return useContentIndex(href, key, options);
};

/**
 * Retrieve and update the entrydate for the modelcatalog
 */
export const useEntryDate = (): ({
  entryDate: ISO_DATE,
  setEntryDate: (entryDate: ISO_DATE) => UpdateEntryDateAction,
}) => {
  const dispatch = useDispatch();
  const entryDate = useSelector(getEntryDate);

  return {
    entryDate,
    setEntryDate: (date: ISO_DATE) => dispatch(updateEntryDate(date)),
  };
};

/**
 */
export const useRelatedConcepts = (
  href: string | Href,
  key?: string = "relatedconcepts",
  options?: UseModularUIBasicOptions<ConceptIndexModel>,
): ?ConceptIndexModel => {
  const { entryDate } = useEntryDate();
  const url = filterParameters(href, ["modelcategory"]);
  url.setParameter("entryDate", entryDate);

  return useModularUIBasic(key, url, {
    expectedModels: ["ConceptIndex"],
    targetModel: ConceptIndexModel,
    ...options,
  });
};
